import { cx } from "@emotion/css";
import { CircleAlert, Flag, Pause } from "lucide-react";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { generatePath, useHistory } from "react-router-dom";
import routing from "routing";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import {
	type FinishExamData,
	FinishExamDefaultModal,
} from "src/components/Modals/FinishExamModals/FinishExamDefaultModal";
import { usePatchExam } from "src/hooks/useExam";
import { useFontSize } from "src/hooks/useFontSize";
import { ExamState, ExamStatus, ExamTypes } from "src/types/enums";

interface UnfinishedExamModalViewProps {
	exam: FinishExamData;
	onClose: () => void;
}

type Choice = "later" | "finish";

export const UnfinishedExamModalView = ({
	exam,
	onClose,
}: UnfinishedExamModalViewProps) => {
	const isCourse = !!exam.courseId && exam.type === ExamTypes.COURSE;
	const { t } = useTranslation();
	const { modalTextFontSizeClass, modalHeaderFontSizeClass } = useFontSize();
	const { openModal } = useModals();
	const history = useHistory();
	const [choice, setChoice] = useState<Choice>("later");
	const { mutateAsync: patchExam } = usePatchExam() as unknown as {
		mutateAsync: (values: {
			id: string;
			status?: string;
			state?: string;
		}) => Promise<void>;
	};

	const finishExam = async () => {
		await patchExam({ id: exam.id, status: ExamStatus.FINISHED });
		onClose();
		openModal(ModalsNames.ExamSummaryModal, {
			examId: exam.id,
		});
	};

	const goToHome = async () => {
		onClose();
		await patchExam({
			id: exam.id,
			state: ExamState.PAUSED,
		});
		if (isCourse) {
			history.push(generatePath(routing.paths.course, { id: exam.courseId }));
		} else {
			history.push("/");
		}
	};

	const isLater = choice === "later";
	const finishLabel = isCourse
		? t("modals.finishExam.finishLesson", "Zakończ lekcję")
		: t("modals.finishExam.finish", "Zakończ egzamin");

	return (
		<FinishExamDefaultModal
			onClose={onClose}
			exam={exam}
			titleText={
				isCourse
					? t("modals.finishExam.lessonTitle", "Wychodzisz z lekcji")
					: t("modals.finishExam.title", "Wychodzisz z egzaminu")
			}
			info={
				<>
					<CircleAlert className="icon-small icon-primary-orange flex-shrink-0" />
					<span>
						<Trans
							i18nKey="modals.finishExam.unresolved"
							values={{
								resolved: exam.stats.resolvedQuestions,
								total: exam.stats.totalQuestions,
							}}
							components={[<b className="text-black" key="count" />]}
						/>
					</span>
				</>
			}
			primaryText={
				isLater
					? t("modals.finishExam.saveAndExit", "Zapisz i wyjdź")
					: finishLabel
			}
			onPrimary={isLater ? goToHome : finishExam}
		>
			<div className="finish-exam-modal--options">
				<button
					type="button"
					data-test-id="finish-exam-option-later"
					aria-pressed={isLater}
					className="finish-exam-modal--option"
					onClick={() => setChoice("later")}
				>
					<span className="flex items-center justify-between w-100">
						<span className="finish-exam-modal--option-icon">
							<Pause className="icon-small icon-primary" />
						</span>
						<span className="finish-exam-modal--recommended">
							{t("modals.finishExam.recommended", "Zalecane")}
						</span>
					</span>
					<span className={modalHeaderFontSizeClass}>
						{t("modals.finishExam.later", "Dokończ później")}
					</span>
					<span className={cx(modalTextFontSizeClass, "text-dark-grey")}>
						{t(
							"modals.finishExam.laterDescription",
							"Zapiszemy Twoje postępy. Wrócisz do egzaminu w dowolnym momencie.",
						)}
					</span>
				</button>
				<button
					type="button"
					data-test-id="finish-exam-option-finish"
					aria-pressed={!isLater}
					className="finish-exam-modal--option finish-exam-modal--option-danger"
					onClick={() => setChoice("finish")}
				>
					<span className="finish-exam-modal--option-icon finish-exam-modal--option-icon-red">
						<Flag className="icon-small icon-error" />
					</span>
					<span className={modalHeaderFontSizeClass}>{finishLabel}</span>
					<span className={cx(modalTextFontSizeClass, "text-dark-grey")}>
						{t(
							"modals.finishExam.finishDescription",
							"Zobaczysz wynik. Na pozostałe pytania nie będzie można już odpowiedzieć.",
						)}
					</span>
				</button>
			</div>
		</FinishExamDefaultModal>
	);
};
